import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Skeleton, Snackbar, Alert, Tabs, Tab, Tooltip,
} from '@mui/material';
import { CheckOutlined, CloseOutlined, WarningAmberOutlined, InfoOutlined } from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { AdminRequest } from '../types';
import { formatDate } from '../utils/datetime';

const RequestsPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const { requestId: urlRequestId } = useParams<{ requestId: string }>();
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const isDark = mode === 'dark';

  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#888888' : '#666666';
  const textM  = isDark ? '#555555' : '#AAAAAA';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const subBg  = isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC';

  const [requests, setRequests]     = useState<AdminRequest[]>([]);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState<'pending' | 'all'>('pending');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewing, setReviewing]   = useState(false);
  const [actionTarget, setActionTarget] = useState<{ req: AdminRequest; action: 'approve' | 'reject' } | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({
    open: false, msg: '', severity: 'success',
  });

  const showSnack = (msg: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.listRequests(tab === 'pending' ? { status: 'pending' } : undefined);
      setRequests(Array.isArray(data) ? data : []);
      // Mark all pending requests as viewed when user opens this page
      if (tab === 'pending') {
        await apiService.markRequestsViewed().catch(() => {}); // Silent fail
      }
    } catch { showSnack('Failed to load requests', 'error'); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll and highlight when URL contains a requestId
  useEffect(() => {
    if (!urlRequestId || requests.length === 0) return;
    // If the request is not in the current tab (pending), switch to all
    const found = requests.find(r => r.request_id === urlRequestId);
    if (!found && tab === 'pending') { setTab('all'); return; }
    const el = rowRefs.current[urlRequestId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRequestId, requests]);

  const openReview = (req: AdminRequest, action: 'approve' | 'reject') => {
    setActionTarget({ req, action });
    setReviewNote('');
    setReviewOpen(true);
  };

  const submitReview = async () => {
    if (!actionTarget) return;
    const { req, action } = actionTarget;
    setReviewing(true);
    try {
      if (action === 'approve') {
        await apiService.approveRequest(req.request_id, reviewNote || undefined);
        showSnack(`Request approved`, 'success');
      } else {
        await apiService.rejectRequest(req.request_id, reviewNote || undefined);
        showSnack('Request rejected', 'info');
      }
      setReviewOpen(false);
      load();
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Action failed', 'error');
    } finally { setReviewing(false); }
  };

  const handleConfirmDeletion = async (req: AdminRequest) => {
    if (!window.confirm(`You are the second approver.\nThis will PERMANENTLY DELETE "${req.app_name}". Confirm?`)) return;
    try {
      const res = await apiService.initiateAppDeletion(req.app_id);
      showSnack(res.message, res.message?.includes('deleted') ? 'success' : 'info');
      load();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Action failed', 'error'); }
  };

  const handleCancelDeletion = async (req: AdminRequest) => {
    if (!window.confirm(`Cancel the pending deletion of "${req.app_name}"?`)) return;
    try {
      await apiService.cancelAppDeletion(req.app_id);
      showSnack('Deletion request cancelled', 'success');
      load();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to cancel', 'error'); }
  };

  const handleConfirmRename = async (req: AdminRequest) => {
    if (!req.new_app_name) { showSnack('Missing target name on rename request', 'error'); return; }
    if (!window.confirm(`You are the second approver.\nThis will rename "${req.app_name}" to "${req.new_app_name}". Confirm?`)) return;
    try {
      const res = await apiService.initiateAppRename(req.app_id, req.new_app_name);
      showSnack(res.message, res.message?.includes('renamed') ? 'success' : 'info');
      load();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Action failed', 'error'); }
  };

  const handleCancelRename = async (req: AdminRequest) => {
    if (!window.confirm(`Cancel the pending rename of "${req.app_name}"?`)) return;
    try {
      await apiService.cancelAppRename(req.app_id);
      showSnack('Rename request cancelled', 'success');
      load();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to cancel', 'error'); }
  };

  const handleConfirmAssistantModel = async (req: AdminRequest) => {
    if (!req.new_model) { showSnack('Missing target model on request', 'error'); return; }
    if (!window.confirm(
      `You are the second approver.\nThis will switch the assistant model from ` +
      `"${req.current_model || '—'}" to "${req.new_model}". Confirm?`,
    )) return;
    try {
      const res = await apiService.initiateOrConfirmAssistantModelChange(req.new_model);
      showSnack(res.message, res.message?.includes('changed') ? 'success' : 'info');
      load();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Action failed', 'error'); }
  };

  const handleCancelAssistantModel = async () => {
    if (!window.confirm('Cancel the pending assistant model change?')) return;
    try {
      await apiService.cancelAssistantModelChange();
      showSnack('Assistant model change cancelled', 'success');
      load();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to cancel', 'error'); }
  };

  const statusChip = (s: string) => {
    const map: Record<string, { label: string; bg: string; color: string }> = {
      pending:   { label: 'Pending',   bg: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7', color: '#D97706' },
      approved:  { label: 'Approved',  bg: isDark ? 'rgba(34,197,94,0.12)'  : '#dcfce7', color: '#16A34A' },
      rejected:  { label: 'Rejected',  bg: isDark ? 'rgba(239,68,68,0.12)'  : '#fee2e2', color: '#DC2626' },
      cancelled: { label: 'Cancelled', bg: isDark ? 'rgba(255,255,255,0.06)': '#f3f4f6', color: textS },
    };
    const c = map[s] ?? { label: s, bg: subBg, color: textS };
    return <Chip label={c.label} size="small" sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600, fontSize: '0.68rem', height: 20, border: 'none' }} />;
  };

  const typeLabel = (t: string) => {
    const m: Record<string, string> = {
      token_limit_increase: 'Token Limit',
      model_access: 'Model Access',
      app_deletion: 'App Deletion',
      app_rename: 'App Rename',
      assistant_model_change: 'Assistant Model',
    };
    return m[t] ?? t;
  };

  const isSuper = user?.role === 'super_admin';

  const sectionLabel = { fontSize: '0.7rem', color: textM, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: textP, letterSpacing: '-0.02em' }}>
            Requests
          </Typography>
          <Typography sx={{ color: textS, fontSize: '0.875rem', mt: 0.25 }}>
            Token limit increases, model access, and app deletion approvals
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={load} sx={{ borderColor: border, color: textS }}>
          Refresh
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: `1px solid ${border}`, minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, fontSize: '0.8rem', textTransform: 'none', color: textS },
          '& .Mui-selected': { color: textP, fontWeight: 600 },
          '& .MuiTabs-indicator': { bgcolor: '#FF1A50' },
        }}
      >
        <Tab label="Pending" value="pending" />
        <Tab label="All Requests" value="all" />
      </Tabs>

      <Box sx={{
        mb: 2, px: 3, py: 1.5,
        bgcolor: isDark ? 'rgba(245,158,11,0.07)' : 'rgba(245,158,11,0.06)',
        border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px',
        display: 'flex', gap: 1.5, alignItems: 'flex-start',
      }}>
        <InfoOutlined sx={{ fontSize: 14, color: '#F59E0B', mt: 0.2, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', color: '#F59E0B', lineHeight: 1.7 }}>
          <strong>1 approval</strong> (any admin): <strong>Token Limit</strong>, <strong>Model Access</strong> — click Approve and it applies immediately.<br />
          <strong>2 super admin approvals</strong>: <strong>App Rename</strong>, <strong>App Deletion</strong>, <strong>Assistant Model</strong> — the initiating super admin
          starts it, then a <em>different</em> super admin must confirm from the app's own screen (not the Approve
          button here) before the change actually applies.
        </Typography>
      </Box>

      <Paper sx={{ border: `1px solid ${border}`, boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Type', 'App', 'Requested By', 'Details', 'Status', 'Date', ''].map(h => (
                  <TableCell key={h} align={h === '' ? 'right' : 'left'}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 6 ? 80 : '80%'} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: textM }}>
                    {tab === 'pending' ? 'No pending requests.' : 'No requests found.'}
                  </TableCell>
                </TableRow>
              ) : requests.map((req) => (
                <TableRow
                  key={req.request_id}
                  ref={el => { rowRefs.current[req.request_id] = el; }}
                  sx={{
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' },
                    ...(urlRequestId === req.request_id && {
                      bgcolor: isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.07)',
                      outline: `2px solid ${isDark ? 'rgba(59,130,246,0.4)' : 'rgba(59,130,246,0.3)'}`,
                      outlineOffset: '-2px',
                    }),
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {req.type === 'app_deletion' && <WarningAmberOutlined sx={{ fontSize: 14, color: '#D97706' }} />}
                      <Typography sx={{ fontSize: '0.8rem', color: req.type === 'app_deletion' ? '#D97706' : req.type === 'app_rename' ? '#3B82F6' : textP, fontWeight: (req.type === 'app_deletion' || req.type === 'app_rename') ? 600 : 400 }}>
                        {typeLabel(req.type)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: textP }}>{req.app_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.78rem', color: textS }}>{req.requested_by_name || req.requested_by}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: textM }}>{req.requested_by}</Typography>
                  </TableCell>
                  <TableCell>
                    {req.type === 'token_limit_increase' && (
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: textS }}>
                          Input: {req.current_input_limit?.toLocaleString()} → <Box component="span" sx={{ color: '#22C55E', fontWeight: 600 }}>{req.requested_input_limit?.toLocaleString()}</Box>
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: textS }}>
                          Output: {req.current_output_limit?.toLocaleString()} → <Box component="span" sx={{ color: '#22C55E', fontWeight: 600 }}>{req.requested_output_limit?.toLocaleString()}</Box>
                        </Typography>
                        {req.reason && <Typography sx={{ fontSize: '0.7rem', color: textM, mt: 0.25, fontStyle: 'italic' }}>{req.reason}</Typography>}
                      </Box>
                    )}
                    {req.type === 'model_access' && (
                      <Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.25 }}>
                          {(req.requested_models ?? []).map(m => (
                            <Chip key={m} label={m} size="small" sx={{ bgcolor: subBg, color: textS, fontSize: '0.68rem', height: 18, border: 'none' }} />
                          ))}
                        </Box>
                        {req.reason && <Typography sx={{ fontSize: '0.7rem', color: textM, fontStyle: 'italic' }}>{req.reason}</Typography>}
                      </Box>
                    )}
                    {req.type === 'app_deletion' && (
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#D97706' }}>
                          {req.approvals?.length ?? 0} / {req.approvals_needed ?? 2} approvals
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: textM }}>
                          Approved by: {(req.approvals ?? []).join(', ')}
                        </Typography>
                      </Box>
                    )}
                    {req.type === 'app_rename' && (
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: textS }}>
                          Rename to: <Box component="span" sx={{ color: '#3B82F6', fontWeight: 600 }}>{req.new_app_name}</Box>
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#3B82F6' }}>
                          {req.approvals?.length ?? 0} / {req.approvals_needed ?? 2} approvals
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: textM }}>
                          Approved by: {(req.approvals ?? []).join(', ')}
                        </Typography>
                        {req.reason && <Typography sx={{ fontSize: '0.7rem', color: textM, mt: 0.25, fontStyle: 'italic' }}>{req.reason}</Typography>}
                      </Box>
                    )}
                    {req.type === 'assistant_model_change' && (
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: textS }}>
                          Model: <Box component="span" sx={{ fontFamily: 'monospace' }}>{req.current_model || '—'}</Box> → <Box component="span" sx={{ color: '#8B5CF6', fontWeight: 600, fontFamily: 'monospace' }}>{req.new_model}</Box>
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#8B5CF6' }}>
                          {req.approvals?.length ?? 0} / {req.approvals_needed ?? 2} approvals
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: textM }}>
                          Approved by: {(req.approvals ?? []).join(', ')}
                        </Typography>
                        {req.reason && <Typography sx={{ fontSize: '0.7rem', color: textM, mt: 0.25, fontStyle: 'italic' }}>{req.reason}</Typography>}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{statusChip(req.status)}</TableCell>
                  <TableCell sx={{ color: textS, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {formatDate(req.created_at, user?.timezone)}
                  </TableCell>
                  <TableCell align="right">
                    {req.status === 'pending' && (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {req.type === 'app_deletion' ? (
                          <>
                            {isSuper && !(req.approvals ?? []).includes(user?.email ?? '') && (
                              <Tooltip title="Confirm deletion as second approver" arrow>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleConfirmDeletion(req)}
                                  sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' }, fontSize: '0.72rem', py: 0.25, px: 1 }}
                                >
                                  Confirm Delete
                                </Button>
                              </Tooltip>
                            )}
                            {isSuper && (req.approvals ?? []).includes(user?.email ?? '') && (
                              <Typography sx={{ fontSize: '0.72rem', color: textM, py: 0.5 }}>You approved</Typography>
                            )}
                            {isSuper && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleCancelDeletion(req)}
                                sx={{ fontSize: '0.72rem', py: 0.25, px: 1, borderColor: border, color: textS }}
                              >
                                Cancel
                              </Button>
                            )}
                          </>
                        ) : req.type === 'app_rename' ? (
                          <>
                            {isSuper && !(req.approvals ?? []).includes(user?.email ?? '') && (
                              <Tooltip title="Confirm rename as second approver" arrow>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleConfirmRename(req)}
                                  sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, fontSize: '0.72rem', py: 0.25, px: 1 }}
                                >
                                  Confirm Rename
                                </Button>
                              </Tooltip>
                            )}
                            {isSuper && (req.approvals ?? []).includes(user?.email ?? '') && (
                              <Typography sx={{ fontSize: '0.72rem', color: textM, py: 0.5 }}>You approved</Typography>
                            )}
                            {isSuper && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleCancelRename(req)}
                                sx={{ fontSize: '0.72rem', py: 0.25, px: 1, borderColor: border, color: textS }}
                              >
                                Cancel
                              </Button>
                            )}
                          </>
                        ) : req.type === 'assistant_model_change' ? (
                          <>
                            {isSuper && !(req.approvals ?? []).includes(user?.email ?? '') && (
                              <Tooltip title="Confirm assistant model change as second approver" arrow>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleConfirmAssistantModel(req)}
                                  sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' }, fontSize: '0.72rem', py: 0.25, px: 1 }}
                                >
                                  Confirm Model
                                </Button>
                              </Tooltip>
                            )}
                            {isSuper && (req.approvals ?? []).includes(user?.email ?? '') && (
                              <Typography sx={{ fontSize: '0.72rem', color: textM, py: 0.5 }}>You approved</Typography>
                            )}
                            {isSuper && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCancelAssistantModel}
                                sx={{ fontSize: '0.72rem', py: 0.25, px: 1, borderColor: border, color: textS }}
                              >
                                Cancel
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            {(user?.role === 'admin' || isSuper) && (
                              <>
                                <Tooltip title="Approve and apply" arrow>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => openReview(req, 'approve')}
                                    startIcon={<CheckOutlined sx={{ fontSize: 13 }} />}
                                    sx={{ fontSize: '0.72rem', py: 0.25, px: 1 }}
                                  >
                                    Approve
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Reject request" arrow>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openReview(req, 'reject')}
                                    startIcon={<CloseOutlined sx={{ fontSize: 13 }} />}
                                    sx={{ fontSize: '0.72rem', py: 0.25, px: 1, borderColor: border, color: textS }}
                                  >
                                    Reject
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                    {req.status !== 'pending' && req.reviewed_by && (
                      <Typography sx={{ fontSize: '0.7rem', color: textM }}>{req.reviewed_by}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: textP }}>
          {actionTarget?.action === 'approve' ? 'Approve Request' : 'Reject Request'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {actionTarget && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: subBg, borderRadius: '6px', border: `1px solid ${border}` }}>
              <Typography sx={{ ...sectionLabel, mb: 0.5 }}>Request</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: textP }}>
                {typeLabel(actionTarget.req.type)} — {actionTarget.req.app_name}
              </Typography>
            </Box>
          )}
          <TextField
            label="Note (optional)"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={reviewNote}
            onChange={e => setReviewNote(e.target.value)}
            placeholder="Add a note for the requester…"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReviewOpen(false)} disabled={reviewing} sx={{ color: textS }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={reviewing}
            onClick={submitReview}
            sx={actionTarget?.action === 'reject' ? { bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } } : {}}
          >
            {reviewing ? 'Saving…' : actionTarget?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestsPage;
